/**
 * Orchestrate trip → Mapbox Map Matching traffic enrichment.
 * Portable entrypoint: Valhalla trip OR Mapbox Directions-shaped route/response.
 *
 * Primary API:
 *   enrichTripWithTraffic(trip, options) → traffic-enhanced result
 */

import { densifyRoutePoints } from './densifyRoutePoints'
import {
  postMapMatching,
  buildTimestamps,
  createQueue,
  resolveDepartureMs,
  resolveDepartAtIso,
  shouldSendDepartAt,
  isNearNowDeparture,
  formatMapboxDepartAt,
  stitchMatchings,
} from './mapMatchingClient'
import {
  getLocalDateTimeInputValue,
  localDateTimeInputToUtcIso,
  localDateTimeInputToValhallaUtc,
  toApiDateTime,
} from './dateTimeLocal'
import { computeMatchingFactor } from './matchingFactor'
import {
  normalizeTrip,
  detectTripFormat,
  METERS_PER_MILE,
} from './normalizeTrip'
import { applyPartialCoverage } from './partialCoverage'
import {
  summarizeCongestion,
  colorForEtaDelta,
  assessTrafficSituation,
  classifyDelayVsBaseline,
  classifyPredictedCongestionLevel,
  CONGESTION_COLORS,
  TRAFFIC_LEGEND,
  MAP_CONGESTION_LEGEND,
  allocateAnnotationsToManeuvers,
  buildCongestionSegments,
  compareLegTotals,
} from './compareTraffic'
import {
  buildTimeDelta,
  formatSignedDuration,
  formatPercent,
  percentDelta,
  mpsToMph,
  maxspeedToMph,
} from './formatters'

export {
  densifyRoutePoints,
  postMapMatching,
  buildTimestamps,
  createQueue,
  resolveDepartureMs,
  resolveDepartAtIso,
  shouldSendDepartAt,
  isNearNowDeparture,
  formatMapboxDepartAt,
  stitchMatchings,
  getLocalDateTimeInputValue,
  localDateTimeInputToUtcIso,
  localDateTimeInputToValhallaUtc,
  toApiDateTime,
  computeMatchingFactor,
  normalizeTrip,
  detectTripFormat,
  applyPartialCoverage,
  allocateAnnotationsToManeuvers,
  buildCongestionSegments,
  compareLegTotals,
  colorForEtaDelta,
  assessTrafficSituation,
  classifyDelayVsBaseline,
  classifyPredictedCongestionLevel,
  CONGESTION_COLORS,
  TRAFFIC_LEGEND,
  MAP_CONGESTION_LEGEND,
  buildTimeDelta,
  formatSignedDuration,
  formatPercent,
  percentDelta,
  mpsToMph,
  maxspeedToMph,
  METERS_PER_MILE,
}

export const TRAFFIC_PROFILES = new Set(['autodp', 'truckdp'])

export function isTrafficProfile(profile) {
  return TRAFFIC_PROFILES.has(profile)
}

function createClientUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `traffic_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 10)}`
}

/**
 * Stable-ish hash of trip shapes + departure for cache keys.
 *
 * @param {object} trip
 * @param {{ type?: number, value?: string }|string|null} dateTimeOrDepartAt
 *   Legacy: pass `dateTime`. Prefer options object via third arg or
 *   `tripTrafficCacheKey(trip, { departAt, dateTime })`.
 * @param {{ departAt?: string|null, dateTime?: object }} [options]
 */
export function tripTrafficCacheKey(
  trip,
  dateTimeOrDepartAt = null,
  options = {}
) {
  let dateTime = null
  let departAt = null

  if (
    dateTimeOrDepartAt &&
    typeof dateTimeOrDepartAt === 'object' &&
    (Object.prototype.hasOwnProperty.call(dateTimeOrDepartAt, 'departAt') ||
      Object.prototype.hasOwnProperty.call(dateTimeOrDepartAt, 'dateTime') ||
      Object.prototype.hasOwnProperty.call(dateTimeOrDepartAt, 'type'))
  ) {
    if (
      Object.prototype.hasOwnProperty.call(dateTimeOrDepartAt, 'type') &&
      !Object.prototype.hasOwnProperty.call(dateTimeOrDepartAt, 'departAt')
    ) {
      dateTime = dateTimeOrDepartAt
    } else {
      departAt = dateTimeOrDepartAt.departAt ?? null
      dateTime = dateTimeOrDepartAt.dateTime ?? null
    }
  } else if (typeof dateTimeOrDepartAt === 'string') {
    departAt = dateTimeOrDepartAt
  } else {
    dateTime = dateTimeOrDepartAt
  }

  if (options && typeof options === 'object') {
    departAt = options.departAt ?? departAt
    dateTime = options.dateTime ?? dateTime
  }

  const normalized = normalizeTrip(trip)
  const legs = normalized.ok ? normalized.legs : []
  const shapes = legs
    .map((leg) =>
      (leg.coordinates || [])
        .map(([lon, lat]) => `${lon.toFixed(5)},${lat.toFixed(5)}`)
        .join(';')
    )
    .join('|')
  const departIso = resolveDepartAtIso({ departAt, dateTime })
  const dt = departIso || `${dateTime?.type ?? -1}:${dateTime?.value ?? ''}`
  let hash = 0
  const key = `${shapes}::${dt}`
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0
  }
  // v4: multi-matching stitch + departAt / distance-weighted timestamps
  return `t4_${hash}`
}

/**
 * Enrich a single normalized leg via Map Matching + partial coverage.
 *
 * @param {object} normalizedLeg
 * @param {object} [options]
 * @param {string} options.accessToken
 * @param {string|null} [options.departAt] ISO-8601 departure (Zulu recommended)
 * @param {{ type?: number, value?: string }} [options.dateTime] UI datetime (fallback)
 * @param {number} [options.departOffsetSec] Extra seconds after departAt for this leg
 * @param {typeof fetch} [options.fetchImpl]
 * @param {Function} [options.enqueue]
 */
export async function enrichNormalizedLeg(normalizedLeg, options = {}) {
  const {
    accessToken,
    departAt = null,
    dateTime = { type: -1 },
    departOffsetSec = 0,
    fetchImpl = fetch,
    enqueue = (fn) => fn(),
  } = options

  if (!accessToken) {
    return {
      ok: false,
      code: 'missing_token',
      error: 'Mapbox access token is missing',
      coordinateCount: normalizedLeg.coordinateCount || 0,
    }
  }

  const densified = densifyRoutePoints(normalizedLeg)
  if (!densified.ok) {
    return {
      ok: false,
      code: densified.code,
      error: densified.message || densified.code,
      coordinateCount: densified.coordinateCount,
      seedCount: densified.seedCount,
      densified,
      shapeCoordinateCount: normalizedLeg.coordinateCount,
    }
  }

  // Shift departAt by prior-leg offset for multi-leg depart_at.
  let effectiveDepartAt = departAt
  const baseIso = resolveDepartAtIso({ departAt, dateTime })
  if (baseIso && departOffsetSec > 0) {
    effectiveDepartAt = new Date(
      Date.parse(baseIso) + departOffsetSec * 1000
    ).toISOString()
  } else if (baseIso && !departAt) {
    // Normalize UI dateTime into ISO Zulu when present
    effectiveDepartAt = baseIso
  }

  // Mapbox Matching `depart_at` when leaving now or at a specific time
  const mapboxDepartAt = shouldSendDepartAt({ departAt, dateTime })
    ? formatMapboxDepartAt(effectiveDepartAt || baseIso)
    : null

  const nearNow = isNearNowDeparture({
    departAt: effectiveDepartAt || departAt,
    dateTime,
  })
  const mapColorMode = nearNow ? 'live' : 'predicted'

  const match = await enqueue(() =>
    postMapMatching({
      accessToken,
      coordinates: densified.coordinates,
      departAt: mapboxDepartAt,
      fetchImpl,
    })
  )

  if (!match.ok) {
    return {
      ok: false,
      code: match.code,
      error: match.message || match.code,
      coordinateCount: densified.coordinateCount,
      seedCount: densified.seedCount,
      densified,
      shapeCoordinateCount: normalizedLeg.coordinateCount,
      departAt: effectiveDepartAt,
      mapColorMode,
      rawMatch: match.raw,
    }
  }

  const factor = computeMatchingFactor(densified.geometry, match.geometry, {
    confidence: match.confidence,
    mapboxDistanceM: match.distance,
    valhallaLengthMiles:
      normalizedLeg.distanceM != null
        ? normalizedLeg.distanceM / METERS_PER_MILE
        : null,
  })

  const partial = applyPartialCoverage({
    normalizedLeg,
    mapboxGeometry: match.geometry,
    annotations: match.annotations,
    mapboxDuration: match.duration,
    mapboxDistanceM: match.distance,
    matchingFactor: factor.matchingFactor,
    matchingDetails: factor.details,
    mapColorMode,
  })

  const congestionSummary = summarizeCongestion(
    match.annotations?.congestion || []
  )

  const mapboxLatLngs = (match.geometry?.coordinates || []).map(
    ([lon, lat]) => [lat, lon]
  )

  const hybridTime = partial.hybridTime
  const baselineTime = partial.baselineTime
  const delta = buildTimeDelta(baselineTime, hybridTime)
  const situation = partial.trafficUnavailable
    ? {
        kind: 'unavailable',
        color: 'unavailable',
        label: 'Traffic unavailable',
        etaSec: hybridTime,
        baselineSec: baselineTime,
        trafficDelaySec: 0,
        baselineDelta: delta,
      }
    : assessTrafficSituation({
        annotations: match.annotations,
        etaSec: hybridTime,
        baselineSec: baselineTime,
      })

  return {
    ok: true,
    code: partial.trafficUnavailable ? 'partial_unavailable' : 'Ok',
    trafficUnavailable: partial.trafficUnavailable,
    coordinateCount: densified.coordinateCount,
    shapeCoordinateCount: normalizedLeg.coordinateCount,
    seedCount: densified.seedCount,
    matchingFactor: factor.matchingFactor,
    pathDiverged: factor.pathDiverged || partial.trafficUnavailable,
    matchingDetails: factor.details,
    coveragePct: partial.coveragePct,
    matchedTime: partial.matchedTime,
    fallbackTime: partial.fallbackTime,
    mapboxGeometry: match.geometry,
    mapboxLatLngs,
    congestionSegments: partial.congestionSegments,
    valhalla: {
      time: baselineTime,
      lengthM: partial.baselineDistanceM,
      length:
        partial.baselineDistanceM != null
          ? partial.baselineDistanceM / METERS_PER_MILE
          : null,
    },
    traffic: {
      time: hybridTime,
      lengthM: partial.hybridDistanceM,
      lengthMiles:
        partial.hybridDistanceM != null
          ? partial.hybridDistanceM / METERS_PER_MILE
          : null,
      congestionSummary,
      matchedTime: partial.matchedTime,
      fallbackTime: partial.fallbackTime,
      mapboxDuration: match.duration,
      mapboxDistanceM: match.distance,
      situation,
      freeFlowSec: situation.freeFlowSec ?? null,
      trafficDelaySec: situation.trafficDelaySec ?? 0,
    },
    delta,
    color: situation.color,
    situation,
    maneuvers: partial.maneuvers,
    annotations: match.annotations,
    densified,
    departAt: effectiveDepartAt,
    mapboxDepartAt,
    mapColorMode,
    matchingCount: match.matchingCount ?? 1,
    unmatchedTracepointCount: match.unmatchedTracepointCount ?? 0,
    stitched: Boolean(match.stitched),
    debug: {
      ...partial.debug,
      situationKind: situation.kind,
      trafficDelaySec: situation.trafficDelaySec,
      freeFlowSec: situation.freeFlowSec,
      matchingCount: match.matchingCount ?? 1,
      unmatchedTracepointCount: match.unmatchedTracepointCount ?? 0,
      stitched: Boolean(match.stitched),
      timestampsSent: 0,
      departAt: effectiveDepartAt,
      mapboxDepartAt,
      mapColorMode,
      nearNow,
    },
    matchUuid: match.uuid || null,
    rawMatch: match.raw,
  }
}

/**
 * Enrich a single Valhalla-style leg (back-compat).
 */
export async function enrichLegWithTraffic(leg, options = {}) {
  const units = options.units || 'miles'
  const normalized = normalizeTrip({
    legs: [leg],
    units,
    summary: leg.summary,
  })
  if (!normalized.ok || !normalized.legs[0]) {
    return {
      ok: false,
      code: normalized.error || 'normalize_failed',
      error: normalized.error || 'Could not normalize leg',
      coordinateCount: 0,
    }
  }
  return enrichNormalizedLeg(normalized.legs[0], options)
}

/**
 * Attach traffic fields onto a copy of the input trip (same shape as input).
 */
function buildEnhancedTrip(normalized, enrichedLegs, totals, uuid) {
  if (normalized.format === 'mapbox' && normalized.mapboxRoute) {
    const route = {
      ...normalized.mapboxRoute,
      duration: totals.traffic?.time ?? normalized.mapboxRoute.duration,
      // Distance stays baseline (meters)
      distance: totals.baseline?.lengthM ?? normalized.mapboxRoute.distance,
      traffic: {
        uuid,
        totals,
        coveragePct: totals.coveragePct,
        mapColorMode: totals.mapColorMode,
      },
      legs: (normalized.mapboxRoute.legs || []).map((leg, i) => {
        const enriched = enrichedLegs[i]
        return {
          ...leg,
          duration: enriched?.traffic?.time ?? leg.duration,
          distance: enriched?.valhalla?.lengthM ?? leg.distance,
          traffic: enriched
            ? {
                ok: enriched.ok,
                unavailable: enriched.trafficUnavailable,
                coveragePct: enriched.coveragePct,
                matchedTime: enriched.matchedTime,
                fallbackTime: enriched.fallbackTime,
                hybridTime: enriched.traffic?.time,
                color: enriched.color,
                mapColorMode: enriched.mapColorMode,
                congestionSegments: enriched.congestionSegments,
                maneuvers: enriched.maneuvers,
                debug: enriched.debug,
              }
            : null,
          steps: (leg.steps || []).map((step, j) => {
            const mnv = enriched?.maneuvers?.[j]
            if (!mnv) {
              return step
            }
            return {
              ...step,
              duration: mnv.traffic?.unavailable
                ? step.duration
                : mnv.traffic?.time ?? step.duration,
              traffic: mnv,
            }
          }),
        }
      }),
    }

    if (normalized.mapboxResponse) {
      return {
        ...normalized.mapboxResponse,
        uuid: uuid || normalized.mapboxResponse.uuid,
        routes: normalized.mapboxResponse.routes.map((routeItem, idx) =>
          idx === 0 ? route : routeItem
        ),
        traffic: { uuid, totals },
      }
    }
    return route
  }

  // Valhalla trip
  const trip = normalized.original
  return {
    ...trip,
    summary: {
      ...trip.summary,
      time: totals.traffic?.time ?? trip.summary?.time,
      // Keep original length units / value for distance
      length: trip.summary?.length,
    },
    traffic: {
      uuid,
      totals,
      coveragePct: totals.coveragePct,
      mapColorMode: totals.mapColorMode,
    },
    legs: (trip.legs || []).map((leg, i) => {
      const enriched = enrichedLegs[i]
      return {
        ...leg,
        summary: {
          ...leg.summary,
          time: enriched?.traffic?.time ?? leg.summary?.time,
          length: leg.summary?.length,
        },
        traffic: enriched
          ? {
              ok: enriched.ok,
              unavailable: enriched.trafficUnavailable,
              coveragePct: enriched.coveragePct,
              matchedTime: enriched.matchedTime,
              fallbackTime: enriched.fallbackTime,
              hybridTime: enriched.traffic?.time,
              color: enriched.color,
              mapColorMode: enriched.mapColorMode,
              congestionSegments: enriched.congestionSegments,
              maneuvers: enriched.maneuvers,
              debug: enriched.debug,
            }
          : null,
        maneuvers: (leg.maneuvers || []).map((mnv, j) => {
          const tm = enriched?.maneuvers?.[j]
          if (!tm) {
            return mnv
          }
          return {
            ...mnv,
            traffic: tm,
          }
        }),
      }
    }),
  }
}

/**
 * Main entry: enrich a Valhalla trip or Mapbox route/response with traffic.
 *
 * @param {object} trip Valhalla trip | Mapbox route | { routes: [...] } | { trip }
 * @param {object} [options]
 * @param {string} options.accessToken
 * @param {string|null} [options.departAt] ISO-8601 departure (Zulu recommended), e.g. `2026-07-22T20:00:00Z`
 * @param {{ type?: number, value?: string }} [options.dateTime] Web UI datetime (used if departAt omitted)
 * @param {typeof fetch} [options.fetchImpl]
 * @param {number} [options.concurrency]
 * @param {string} [options.cacheKey]
 * @param {number} [options.routeIndex] Mapbox routes[] index (default 0)
 * @returns {Promise<object>} Traffic-enhanced result (includes `.trip`)
 */
export async function enrichTripWithTraffic(trip, options = {}) {
  const {
    accessToken,
    departAt = null,
    dateTime = { type: -1 },
    fetchImpl = fetch,
    concurrency = 2,
    cacheKey = null,
    routeIndex = 0,
  } = options

  const uuid = createClientUuid()
  const sendMapboxDepartAt = shouldSendDepartAt({ departAt, dateTime })
  const resolvedDepartAt = resolveDepartAtIso({ departAt, dateTime })
  // Only pass an ISO departAt into legs when Mapbox should get `depart_at`
  // (leave now / leave at). Arrive-by still uses dateTime for timestamps only.
  const legDepartAt = sendMapboxDepartAt ? resolvedDepartAt || departAt : null

  if (!accessToken) {
    return {
      ok: false,
      code: 'missing_token',
      error: 'Mapbox access token is missing',
      legs: [],
      totals: null,
      uuid,
      trip: null,
      cacheKey,
      departAt: legDepartAt,
    }
  }

  const normalized = normalizeTrip(trip, { routeIndex })
  if (!normalized.ok) {
    return {
      ok: false,
      code: normalized.error || 'normalize_failed',
      error: `Could not normalize trip: ${normalized.error}`,
      legs: [],
      totals: null,
      uuid,
      trip: null,
      inputFormat: normalized.format || null,
      cacheKey,
      departAt: legDepartAt,
    }
  }

  const enqueue = createQueue(concurrency)
  const enrichedLegs = []
  const matchUuids = []
  let departOffsetSec = 0

  for (let i = 0; i < normalized.legs.length; i++) {
    // eslint-disable-next-line no-await-in-loop
    const result = await enrichNormalizedLeg(normalized.legs[i], {
      accessToken,
      departAt: legDepartAt,
      dateTime,
      departOffsetSec,
      fetchImpl,
      enqueue,
    })
    enrichedLegs.push(result)
    if (result.matchUuid) {
      matchUuids.push(result.matchUuid)
    }
    // Next leg starts after this leg's baseline duration (ascending timestamps)
    departOffsetSec += normalized.legs[i].durationSec || 0
  }

  const okLegs = enrichedLegs.filter((leg) => leg.ok)
  const anyOk = okLegs.length > 0

  const totalBaselineTime =
    normalized.durationSec != null
      ? normalized.durationSec
      : enrichedLegs.reduce((sum, leg) => sum + (leg.valhalla?.time || 0), 0)
  const totalBaselineLengthM =
    normalized.distanceM != null
      ? normalized.distanceM
      : enrichedLegs.reduce((sum, leg) => sum + (leg.valhalla?.lengthM || 0), 0)

  // Hybrid: matched traffic times + fallback baseline on uncovered / failed legs
  let hybridTime = 0
  let matchedTimeSum = 0
  let fallbackTimeSum = 0
  const mergedAnnotations = {
    duration: [],
    distance: [],
    congestion: [],
    speed: [],
    maxspeed: [],
  }
  for (let i = 0; i < enrichedLegs.length; i++) {
    const leg = enrichedLegs[i]
    const baseline = normalized.legs[i]?.durationSec || 0
    if (leg.ok) {
      hybridTime += leg.traffic?.time ?? baseline
      matchedTimeSum += leg.matchedTime || 0
      fallbackTimeSum += leg.fallbackTime || 0
      const ann = leg.annotations
      if (ann) {
        for (const key of Object.keys(mergedAnnotations)) {
          if (Array.isArray(ann[key])) {
            mergedAnnotations[key].push(...ann[key])
          }
        }
      }
    } else {
      hybridTime += baseline
      fallbackTimeSum += baseline
    }
  }

  const coveragePctAvg = anyOk
    ? okLegs.reduce((sum, leg) => sum + (leg.coveragePct || 0), 0) /
      okLegs.length
    : 0

  const shapeCoordinateCount = normalized.legs.reduce(
    (sum, leg) => sum + (leg.coordinateCount || 0),
    0
  )
  const densifyCoordinateCount = enrichedLegs.reduce(
    (sum, leg) => sum + (leg.coordinateCount || 0),
    0
  )

  const tripSituation = anyOk
    ? assessTrafficSituation({
        annotations: mergedAnnotations,
        etaSec: hybridTime,
        baselineSec: totalBaselineTime,
      })
    : {
        kind: 'unavailable',
        color: 'unavailable',
        label: 'Traffic unavailable',
        trafficDelaySec: 0,
      }

  const totals = {
    baseline: {
      time: totalBaselineTime,
      lengthM: totalBaselineLengthM,
      lengthMiles: totalBaselineLengthM / METERS_PER_MILE,
    },
    // Back-compat aliases used by web UI
    valhalla: {
      time: totalBaselineTime,
      length: totalBaselineLengthM / METERS_PER_MILE,
      lengthM: totalBaselineLengthM,
    },
    traffic: {
      time: anyOk ? hybridTime : null,
      // Distance always equals baseline
      lengthM: totalBaselineLengthM,
      lengthMiles: totalBaselineLengthM / METERS_PER_MILE,
      matchedTime: matchedTimeSum,
      fallbackTime: fallbackTimeSum,
      freeFlowSec: tripSituation.freeFlowSec ?? null,
      mapboxNoTrafficSec: tripSituation.mapboxNoTrafficSec ?? null,
      mapboxTrafficSec:
        tripSituation.mapboxTrafficSec ?? (anyOk ? hybridTime : null),
      trafficDelaySec: tripSituation.trafficDelaySec ?? 0,
      situation: tripSituation,
    },
    delta: buildTimeDelta(totalBaselineTime, anyOk ? hybridTime : null),
    trafficDelay: tripSituation.trafficDelay || null,
    color: tripSituation.color,
    situationKind: tripSituation.kind,
    situationLabel: tripSituation.label,
    matchingFactorAvg: anyOk
      ? Math.round(
          okLegs.reduce((sum, leg) => sum + (leg.matchingFactor || 0), 0) /
            okLegs.length
        )
      : null,
    coveragePct: Math.round(coveragePctAvg * 10) / 10,
    coordinateCount: densifyCoordinateCount,
    shapeCoordinateCount,
    pathDiverged: enrichedLegs.some((leg) => leg.pathDiverged),
    matchingCountTotal: enrichedLegs.reduce(
      (sum, leg) => sum + (leg.matchingCount || 0),
      0
    ),
    mapColorMode: okLegs[0]?.mapColorMode || 'live',
    legsDebug: enrichedLegs.map((leg, i) => ({
      index: i,
      ok: leg.ok,
      unavailable: leg.trafficUnavailable,
      coveragePct: leg.coveragePct,
      matchingFactor: leg.matchingFactor,
      matchedTime: leg.matchedTime,
      fallbackTime: leg.fallbackTime,
      hybridTime: leg.traffic?.time,
      baselineTime: leg.valhalla?.time,
      mapboxDuration: leg.traffic?.mapboxDuration,
      situationKind: leg.situation?.kind,
      trafficDelaySec: leg.situation?.trafficDelaySec,
      shapeCoords: leg.shapeCoordinateCount,
      densifyCoords: leg.coordinateCount,
      matchingCount: leg.matchingCount ?? null,
      unmatchedTracepointCount: leg.unmatchedTracepointCount ?? null,
      stitched: leg.stitched ?? false,
      departAt: leg.departAt ?? null,
      mapColorMode: leg.mapColorMode ?? null,
      timestampsSent: 0,
      error: leg.error || null,
    })),
  }

  const responseUuid = normalized.uuid || matchUuids[0] || uuid

  const enhancedTrip = anyOk
    ? buildEnhancedTrip(normalized, enrichedLegs, totals, responseUuid)
    : null

  return {
    ok: anyOk,
    partial: anyOk && okLegs.length !== enrichedLegs.length,
    code: anyOk ? 'Ok' : enrichedLegs[0]?.code || 'failed',
    error: anyOk
      ? null
      : enrichedLegs
          .map((leg) => leg.error)
          .filter(Boolean)
          .join('; ') || 'Traffic enrichment failed',
    inputFormat: normalized.format,
    uuid: responseUuid,
    clientUuid: uuid,
    matchUuids,
    departAt: legDepartAt,
    legs: enrichedLegs,
    totals,
    trip: enhancedTrip,
    cacheKey:
      cacheKey ||
      tripTrafficCacheKey(trip, { departAt: legDepartAt, dateTime }),
    legend: TRAFFIC_LEGEND,
    mapLegend: MAP_CONGESTION_LEGEND,
  }
}
